import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import { Settings, ArrowLeft, Plus, Edit, Trash2, AlertTriangle } from 'lucide-react';

export default function HorsesFeedProducts() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newProductName, setNewProductName] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [showInUseConfirm, setShowInUseConfirm] = useState(false);
  const [inUseMessage, setInUseMessage] = useState('');

  const fetchProducts = async () => {
    try {
      const res = await axios.get(getApiUrl('/api/horses/feed/products'));
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const createProduct = async () => {
    const name = (newProductName || '').trim();
    if (!name) return;
    setLoading(true);
    try {
      const res = await axios.post(getApiUrl('/api/horses/feed/products'), { name });
      setProducts(prev => [...prev, res.data]);
      setNewProductName('');
    } catch (error) {
      console.error('Error creating product:', error);
      alert(error?.response?.data?.error || 'Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const updateProduct = async (productId, name) => {
    try {
      const res = await axios.put(getApiUrl(`/api/horses/feed/products/${productId}`), { name });
      setProducts(prev => prev.map(p => p.id === productId ? res.data : p));
    } catch (error) {
      console.error('Error updating product:', error);
      alert(error?.response?.data?.error || 'Failed to update product');
    }
  };

  const requestDelete = (product) => {
    setDeletingProduct(product);
    setShowDeleteConfirm(true);
  };

  const deleteProduct = async () => {
    if (!deletingProduct) return;
    try {
      await axios.delete(getApiUrl(`/api/horses/feed/products/${deletingProduct.id}`));
      setProducts(prev => prev.filter(p => p.id !== deletingProduct.id));
      setShowDeleteConfirm(false);
      setDeletingProduct(null);
    } catch (error) {
      if (error?.response?.status === 409) {
        setInUseMessage(error?.response?.data?.error || 'Cannot delete product: it is currently in use by one or more feed plans.');
        setShowInUseConfirm(true);
        setShowDeleteConfirm(false);
      } else {
        console.error('Error deleting product:', error);
        alert('Failed to delete product');
      }
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl sm:text-2xl font-bold flex items-center">
            <Settings className="w-6 h-6 mr-2" />
            Manage Feed Products
          </h2>
          <button
            onClick={() => navigate('/horses/feedplan')}
            className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50 text-sm whitespace-nowrap self-start"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Return to Feed Plans</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b">
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="New product name"
              value={newProductName}
              onChange={(e) => setNewProductName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createProduct(); }}
              className="border rounded px-3 py-2 flex-1"
            />
            <button
              onClick={createProduct}
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              <Plus className="w-4 h-4 inline-block mr-1" /> Add
            </button>
          </div>
        </div>
        <div className="p-4 space-y-2">
          {products.filter(p => p.is_active).length === 0 ? (
            <p className="text-gray-500 text-center py-8">No products yet. Add your first product above.</p>
          ) : (
            products.filter(p => p.is_active).map(product => (
              <div key={product.id} className="flex items-center justify-between p-3 border rounded">
                <div className="flex-1">
                  {product.__editing ? (
                    <input
                      type="text"
                      value={product.__editName}
                      onChange={(e) => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, __editName: e.target.value } : p))}
                      className="w-full border rounded px-2 py-1"
                      autoFocus
                    />
                  ) : (
                    <span>{product.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {product.__editing ? (
                    <>
                      <button
                        onClick={async () => {
                          const newName = (product.__editName || '').trim();
                          if (!newName) return;
                          await updateProduct(product.id, newName);
                          setProducts(prev => prev.map(p => p.id === product.id ? { ...p, name: newName, __editing: false, __editName: undefined } : p));
                        }}
                        className="px-2 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, __editing: false, __editName: undefined } : p))}
                        className="px-2 py-1 text-sm border rounded"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setProducts(prev => prev.map(p => p.id === product.id ? { ...p, __editing: true, __editName: p.name } : p))}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit product"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => requestDelete(product)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Delete product"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {showDeleteConfirm && deletingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold">Confirm Delete</h3>
            </div>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the product <span className="font-semibold">{deletingProduct.name}</span>?
              This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeletingProduct(null); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteProduct}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showInUseConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="w-6 h-6 text-orange-600 mr-3" />
              <h3 className="text-lg font-semibold">Cannot Delete Product</h3>
            </div>
            <p className="text-gray-700 mb-6">
              {inUseMessage}
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => { setShowInUseConfirm(false); setInUseMessage(''); setDeletingProduct(null); }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


